/**
 * The Techie Gain mark.
 *
 * ## One component, every surface
 *
 * The mark previously appeared as a `Boxes` icon in the marketing header and footer, and as a
 * `ServerCog` on the auth screens — two different placeholder icons standing in for a brand that
 * did not exist yet. Now that there is a real logo, it lives in one file so a future change is one
 * edit rather than a hunt through seven components.
 *
 * ## Why an <img> rather than inline SVG
 *
 * The mark is a real asset the owner supplies, not something the codebase should own the geometry
 * of. Pointing at `/logo.png` means replacing the brand is dropping a file into `public/` — no
 * rebuild of a component, no risk of a hand-transcribed path drifting from the original artwork.
 *
 * The supplied file arrived at 1536x1024 with the artwork occupying 764x845 in the middle. Rendered
 * into a square box that would have been squashed horizontally and shrunk by its own margins, so it
 * was cropped square around the artwork and halved to 473px — ample for a 52px render on a 3x
 * display, and a tenth of the original weight. `logo-original.png` keeps the untouched file.
 *
 * The trade against inline SVG is that the mark cannot inherit `currentColor`, so the file itself
 * has to work on whatever it is sitting on — which is why there are two.
 *
 * ## Two files, swapped in CSS
 *
 * `logo.png` is a white monogram inside a blue shield. On the dark surfaces this platform uses
 * everywhere that reads perfectly; on the white sidebar the light theme gives it, the monogram
 * disappears and all you see is the blue shield curve. `logo-light.png` is the same artwork with
 * the near-white pixels taken to the light theme's own foreground navy — same geometry, same brand
 * blue, legible on white. (`logo-original.png` is not an alternative: it is the untouched source,
 * the same mark on a black ground with a glow.)
 *
 * The swap is CSS, not `useTheme()`. This component renders on the marketing pages, the auth
 * screens and throughout the app, so a hook here would be a hook in all of them — and would paint
 * the wrong mark for a frame before the store rehydrated. Both files ship; the theme decides which
 * is displayed, with no JavaScript involved.
 *
 * The wrapper carries the accessible name and both images are hidden from assistive technology, so
 * the mark is announced once rather than twice.
 */
export function Logo({
  size = 36,
  className = '',
}: {
  size?: number;
  className?: string;
}) {
  const dimensions = { width: size, height: size };

  return (
    <span
      role="img"
      aria-label="Techie Gain"
      className={className}
      style={{ ...dimensions, display: 'inline-block' }}
    >
      <img
        src="/logo.png"
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        className="logo-on-dark"
        style={dimensions}
      />
      <img
        src="/logo-light.png"
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        className="logo-on-light"
        style={dimensions}
      />
    </span>
  );
}

/** The mark beside the name, which is how it appears in a header or a footer. */
export function LogoLockup({
  size = 36,
  subtitle = false,
}: {
  size?: number;
  subtitle?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <Logo size={size} className="shrink-0" />
      {/* min-w-0 so the lockup can compress inside a flex row instead of pushing its neighbours
          off the screen. The subtitle wraps rather than truncating, which is how the dashboard
          sidebar renders the same two lines. */}
      <span className="min-w-0 leading-tight">
        <span className="block truncate font-semibold tracking-tight">Techie Gain</span>
        {subtitle && (
          <span className="text-muted-foreground/80 block text-[11px] leading-tight">
            Infrastructure Operations Platform
          </span>
        )}
      </span>
    </span>
  );
}
